import * as React from 'react';
import { isEmpty, isNil } from 'lodash';
import classnames from 'classnames';
import { connect } from 'react-redux';

import Tooltip from 'components/Tooltip';
import Input from 'components/inputs/input/Input';
import InlineEditingWrapper from 'components/inline-editing-wrapper/InlineEditingWrapper';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import { TableCell, TableRow } from 'components/table';
import DeliverableMediaAttachments from 'modules/deliverables/components/media/parts/DeliverableMediaAttachments';
import DeliverableMediaUploadPlaceholder from 'modules/deliverables/components/media/parts/placeholder/DeliverableMediaUploadPlaceholder';
import DeliverableMetricsGalleryModalAsset from 'modules/admin/components/deliverables/gallery-modal-asset/DeliverableMetricsGalleryModalAsset';
import ImageUploader, { MEDIA_TYPES_IMAGES, MEDIA_TYPES_IMAGES_OR_VIDEOS } from 'components/ImageUploader';

import InlineEditingFieldsContext from '../../../../../context/InlineEditingFieldsContext';

import { getDeliverableAttachmentUploadingID } from 'modules/admin/reducers/admin-additional-deliverable';

import { __t, applyTranslations } from 'utils/language';
import {
  DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS,
  DELIVERABLE_ATTACHMENT_TYPE_HIGHRES,
  filterAttachmentsByType
} from 'utils/deliverables';

import type {
  AdminAnalytic,
  DeliverableAnalytic,
  DeliverableAttachment,
  DeliverableAttachmentType,
  GalleryModalItem
} from 'types';

import './deliverables-metrics-instagram-story-form.scss';

applyTranslations(require('./deliverables-metrics-instagram-story-form.i18n.json'));

type Analytic = {
  reach: number | undefined | null,
  impressions: number | undefined | null,
  interactions: number,
  shares: number,
  profileVisits: number,
  websiteClicks: number,
  stickerTaps: number,
  linkClicks: number,
  nextStoryCount: number,
  exitedCount: number,
  metricsType: string
};

type OwnProps = {
  index: number,
  deliverableId: number,
  analytic: DeliverableAnalytic,
  isFullScreen?: boolean,
  attachments: Array<DeliverableAttachment>,
  canUploadSnippet?: boolean,
  disableDragAndDrop?: boolean,
  hideAnalyticsColumn?: boolean,
  onUpdateDeliverableMetrics: (d: number, c: number, b: number, a: AdminAnalytic) => Promise<void>,
  onDeleteDeliverableMetricsAnalytics: (c: number, b: number, a: number) => void,
  onDeliverableAttachmentUpload?: (c: number, b: DeliverableAttachmentType, a: Array<File>) => void,
  onUnMatchDeliverableAttachment?: (c: number, b: number, a: number) => void
};

type StateProps = {
  uploadingAttachments: Array<string>
};

type Props = OwnProps & StateProps;

type State = {
  metric: Analytic,
  inlineEditingFields: Array<any>
};

class DeliverablesMetricsInstagramStoryAnalyticForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const analytic = this.props.analytic;

    this.state = {
      metric: {
        reach: analytic.reach,
        impressions: analytic.impressions,
        shares: analytic.shares,
        interactions: analytic.interactions,
        profileVisits: analytic.profileVisits,
        websiteClicks: analytic.websiteClicks,
        stickerTaps: analytic.stickerTaps,
        linkClicks: analytic.linkClicks,
        nextStoryCount: analytic.nextStoryCount,
        exitedCount: analytic.exitedCount,
        metricsType: 'instagram_story_metrics'
      },
      inlineEditingFields: []
    };
  }

  getInlineEditingFieldsContext() {
    return {
      registerInlineEditingField: this.registerInlineEditingField,
      unregisterInlineEditingField: this.unregisterInlineEditingField
    };
  }

  isInGlobalInlineEditMode() {
    const { inlineEditingFields } = this.state;

    return !isEmpty(inlineEditingFields);
  }

  registerInlineEditingField = (component: any): void => {
    if (component) {
      this.setState(state => ({
        inlineEditingFields: [...state.inlineEditingFields, component]
      }));
    }
  };

  unregisterInlineEditingField = (component: any): void => {
    if (!component) {
      return;
    }

    this.setState(state => ({
      inlineEditingFields: state.inlineEditingFields.filter(field => field !== component)
    }));
  };

  handleInputChange = (name: string) => {
    const { analytic, deliverableId, onUpdateDeliverableMetrics } = this.props;

    return (value: any) => {
      this.setState(state => ({
        metric: {
          ...state.metric,
          [String(name)]: value && !isNaN(Number(value)) ? Number(value) : 0
        }
      }),
      () => onUpdateDeliverableMetrics(deliverableId, analytic.campaignId, analytic.id, {
        ...this.state.metric,
        saves: undefined,
        likes: undefined,
        comments: undefined
      }));
    };
  };

  handleDelete = () => {
    const { analytic, deliverableId, onDeleteDeliverableMetricsAnalytics } = this.props;

    if (analytic) {
      onDeleteDeliverableMetricsAnalytics(deliverableId, analytic.campaignId, analytic.id);
    }
  };

  handleUploadDeliverableSnippetAttachment = (files: Array<File>) => {
    const { analytic, onDeliverableAttachmentUpload } = this.props;

    if (onDeliverableAttachmentUpload) {
      onDeliverableAttachmentUpload(analytic.id, DELIVERABLE_ATTACHMENT_TYPE_HIGHRES, files);
    }
  };

  handleUploadDeliverableAnalyticAttachment = (files: Array<File>) => {
    const { analytic, onDeliverableAttachmentUpload } = this.props;

    if (onDeliverableAttachmentUpload) {
      onDeliverableAttachmentUpload(analytic.id, DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS, files);
    }
  };

  renderCustomGalleryModalAsset = (item: GalleryModalItem, assetIndex: number, onCloseModal?: (() => void) | null) => {
    const {
      attachments,
      analytic,
      deliverableId,
      disableDragAndDrop,
      onUnMatchDeliverableAttachment,
      onDeleteDeliverableMetricsAnalytics
    } = this.props;

    return (
      <DeliverableMetricsGalleryModalAsset
        item={item}
        index={assetIndex}
        onUnMatch={disableDragAndDrop ? undefined : (
          () => {
            if (onUnMatchDeliverableAttachment) {
              const attachment = attachments.find(att => att.id === item.id);

              if (attachment?.meta.type === 'highres') {
                onDeleteDeliverableMetricsAnalytics(deliverableId, analytic.campaignId, analytic.id);
              } else {
                onUnMatchDeliverableAttachment(deliverableId, Number(item.id), analytic.id);
              }
            }

            if (onCloseModal) {
              // force close since it will close in certain cases anyway on modal being unmounted
              onCloseModal();
            }
          }
        )}
      />
    );
  };

  renderTextView = (value?: number | null) =>
    () => (
      <span>
        {value ?? __t('deliverables_metrics_instagram_story_form.missing_data_placeholder')}
      </span>
    );


  renderTextEdit = () =>
    () => (
      <Input
        focusOnMount
        value="" // will be overwritten in InlineEditingWrapper
        width="responsive"
      />
    );

  renderSnippetUploadPlaceholder = (matchedSnippets: Array<DeliverableAttachment>) => {
    const { analytic, uploadingAttachments } = this.props;
    const entryId = getDeliverableAttachmentUploadingID(analytic.id, DELIVERABLE_ATTACHMENT_TYPE_HIGHRES);

    return (
      <ImageUploader
        key={`uploader-${matchedSnippets.length > 0 ? 'uploading' : 'ready'}`} // force to rerender to upload the same file again in Chrome COLLAB-3929
        multiple
        mediaTypes={MEDIA_TYPES_IMAGES_OR_VIDEOS}
        onUpload={this.handleUploadDeliverableSnippetAttachment}
      >
        <DeliverableMediaUploadPlaceholder
          size="small"
          mode={uploadingAttachments.includes(entryId) ? 'uploading' : 'create'}
        />
      </ImageUploader>
    );
  };

  renderAnalyticUploadPlaceholder = (matchedSnippets: Array<DeliverableAttachment>) => {
    const { analytic, uploadingAttachments } = this.props;
    const entryId = getDeliverableAttachmentUploadingID(analytic.id, DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS);

    return (
      <ImageUploader
        key={`uploader-${matchedSnippets.length > 0 ? 'uploading' : 'ready'}`} // force to rerender to upload the same file again in Chrome COLLAB-3929
        multiple
        mediaTypes={MEDIA_TYPES_IMAGES}
        onUpload={this.handleUploadDeliverableAnalyticAttachment}
      >
        <DeliverableMediaUploadPlaceholder
          size="small"
          mode={uploadingAttachments.includes(entryId) ? 'uploading' : 'create'}
        />
      </ImageUploader>
    );
  };

  render(): React.ReactNode {
    const {
      analytic,
      index,
      isFullScreen,
      attachments,
      canUploadSnippet,
      disableDragAndDrop,
      hideAnalyticsColumn
    } = this.props;
    const { metric } = this.state;
    const inGlobalEditMode = this.isInGlobalInlineEditMode();

    const filteredAttachments = (analytic.matchedAttachments || [])
      .reduce<Array<DeliverableAttachment>>((result, id) => {
        const foundAttachment = attachments.find(attachment => attachment.id === id);
        if (foundAttachment) {
          result.push(foundAttachment);
        }
        return result;
      }, []);
    const matchedSnippets = filterAttachmentsByType(filteredAttachments, DELIVERABLE_ATTACHMENT_TYPE_HIGHRES);
    const matchedAnalytics = filterAttachmentsByType(filteredAttachments, DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS);

    const tdClasses = classnames('deliverables-metrics-instagram-story-form__table-cell', {
      'deliverables-metrics-instagram-story-form__table-cell--is-limited-width': !isFullScreen
    });

    return (
      <InlineEditingFieldsContext.Provider value={this.getInlineEditingFieldsContext()}>
        <TableRow>
          <TableCell width="micro">
            {index}
          </TableCell>
          <TableCell className={tdClasses}>
            <DeliverableMediaAttachments
              mediaWidth={64}
              mediaHeight={64}
              compactDisplay
              canDragAndDrop={!disableDragAndDrop}
              canUpload={Boolean(canUploadSnippet)}
              attachments={matchedSnippets}
              isImagePreviewEnabled
              renderCustomAsset={this.renderCustomGalleryModalAsset}
              placeholderForUpload={canUploadSnippet && !matchedSnippets.length && this.renderSnippetUploadPlaceholder(matchedSnippets)}
            />
          </TableCell>
          <TableCell className={tdClasses}>
            {
              !hideAnalyticsColumn && (
                <DeliverableMediaAttachments
                  mediaWidth={64}
                  mediaHeight={64}
                  deliverableAttachmentType={DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS}
                  canDragAndDrop={!disableDragAndDrop}
                  compactDisplay
                  canUpload={Boolean(canUploadSnippet)}
                  attachments={matchedAnalytics}
                  isImagePreviewEnabled
                  renderCustomAsset={this.renderCustomGalleryModalAsset}
                  placeholderForUpload={canUploadSnippet && !matchedAnalytics.length && this.renderAnalyticUploadPlaceholder(matchedAnalytics)}
                />
              )
            }
          </TableCell>
          <TableCell className={tdClasses}>
            <InlineEditingWrapper
              saveOnBlur
              onSubmit={this.handleInputChange('reach')}
              renderViewMode={this.renderTextView(metric.reach)}
              renderEditMode={this.renderTextEdit()}
              initialValue={isNil(metric.reach) ? '' : String(metric.reach)}
              inGlobalEditMode={inGlobalEditMode}
            />
          </TableCell>
          <TableCell className={tdClasses}>
            <InlineEditingWrapper
              saveOnBlur
              onSubmit={this.handleInputChange('impressions')}
              renderViewMode={this.renderTextView(metric.impressions)}
              renderEditMode={this.renderTextEdit()}
              initialValue={isNil(metric.impressions) ? '' : String(metric.impressions)}
              inGlobalEditMode={inGlobalEditMode}
            />
          </TableCell>
          {
            ['interactions', 'profileVisits', 'websiteClicks', 'stickerTaps', 'linkClicks']
              .map(id => (
                <TableCell
                  key={id}
                  className={tdClasses}
                >
                  <InlineEditingWrapper
                    saveOnBlur
                    onSubmit={this.handleInputChange(id)}
                    renderViewMode={this.renderTextView(metric[id])}
                    renderEditMode={this.renderTextEdit()}
                    initialValue={metric[id] ?? 0}
                    inGlobalEditMode={inGlobalEditMode}
                  />
                </TableCell>
              ))
          }
          <TableCell
            width="fill"
            className={tdClasses}
          >
            <Tooltip
              width="short"
              message={__t('deliverables_metrics_instagram_story_form.button_delete')}
              classNameWrapper="deliverables-metrics-instagram-story-form__tooltip-wrapper"
            >
              <SVGDynamicIcon
                name="trash-custom-fg"
                width={16}
                height={16}
                onClick={this.handleDelete}
              />
            </Tooltip>
          </TableCell>
        </TableRow>
      </InlineEditingFieldsContext.Provider>
    );
  }
}

const mapStateToProps = ({ adminAdditionalDeliverable }): StateProps => ({
  uploadingAttachments: adminAdditionalDeliverable.uploadingIds
});

export default connect(mapStateToProps)(DeliverablesMetricsInstagramStoryAnalyticForm);
